import { useState } from 'react'
import { Crest } from '@/components/Crest'
import { supabase } from '@/lib/supabase'
import { env } from '@/lib/env'

/**
 * Email and password, because that is what everybody here can actually use.
 *
 * Entra was the earlier plan and is kept behind VITE_ENTRA_ENABLED, off by
 * default. It assumed every user has a work account, which reception on a
 * shared terminal does not.
 *
 * THERE IS NO SIGN-UP LINK, and its absence is the design. This system holds
 * immigration status, pay and health data for 171 people. Accounts are created
 * by somebody with admin rights (npm run users:add), which sends an invitation
 * on which the person chooses their own password. Sign-up is off in Supabase
 * and must stay off.
 *
 * The failure message is deliberately vague. "No account with that email"
 * tells anybody who asks which addresses exist here, and a hotel's staff
 * addresses are easy to guess.
 */
export function SignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resetSent, setResetSent] = useState(false)
  const [mode, setMode] = useState<'password' | 'reset'>('password')

  async function withEntra() {
    setError(null)
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: { scopes: 'email', redirectTo: window.location.origin },
    })
    if (err) setError(err.message)
  }

  async function signIn(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password })
    setBusy(false)
    if (err) {
      setError(
        /invalid login credentials/i.test(err.message)
          ? 'That email and password do not match an account. Check both, and if you have never set a password, use the reset link below.'
          : err.message,
      )
    }
  }

  async function sendReset(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin,
    })
    setBusy(false)
    // Reported as sent either way, on purpose: whether an address has an
    // account here is not something an unauthenticated form should confirm.
    if (err && !/rate/i.test(err.message)) setError(err.message)
    else setResetSent(true)
  }

  return (
    <div className="signin">
      <div className="card">
        <div className="crest">
          <Crest width={150} variant="lockup" />
          <div className="wordmark" style={{ marginTop: 4 }}>
            HR &middot; People &amp; Compliance
          </div>
        </div>
        <div className="body">
          {env.entraEnabled && (
            <>
              <button className="btn" type="button" onClick={withEntra} style={{ width: '100%' }}>
                Sign in with your work account
              </button>
              <p className="note" style={{ textAlign: 'center', margin: '14px 0 4px' }}>
                or
              </p>
            </>
          )}

          {resetSent ? (
            <p className="note" style={{ margin: '4px 0 0' }}>
              If {email.trim()} has an account, a link to set a new password is on its way. It
              expires in an hour. Nothing else has changed on the account.
            </p>
          ) : (
            <form onSubmit={mode === 'password' ? signIn : sendReset}>
              <label className="stackfield">
                <span>Work email</span>
                <input
                  type="email"
                  required
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@tlcgroup.co.uk"
                />
              </label>

              {mode === 'password' && (
                <label className="stackfield">
                  <span>Password</span>
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </label>
              )}

              <div className="actions">
                <button className="btn primary" type="submit" disabled={busy}>
                  {busy
                    ? mode === 'password'
                      ? 'Signing in…'
                      : 'Sending…'
                    : mode === 'password'
                      ? 'Sign in'
                      : 'Email me a reset link'}
                </button>
                <button
                  className="btn quiet"
                  type="button"
                  onClick={() => {
                    setMode(mode === 'password' ? 'reset' : 'password')
                    setError(null)
                  }}
                >
                  {mode === 'password' ? 'I need to set a password' : 'Back to signing in'}
                </button>
              </div>
            </form>
          )}

          {error !== null && (
            <p style={{ fontSize: 13, color: 'var(--sev-critical)', margin: '14px 0 0' }}>{error}</p>
          )}

          <p className="note" style={{ marginTop: 18 }}>
            Accounts are created by HR. There is no sign-up: if you have no account, ask HR rather
            than trying to make one.
          </p>
        </div>
      </div>
    </div>
  )
}
